import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Flag, Search } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchFeatureFlags, setFeatureFlag, type AdminFeatureFlag } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'
import { cn } from '@/lib/cn'

/**
 * A14.03 — Feature-flag yoqish/o'chirish. Har flag'ni jonli `POST /admin/settings/flags`
 * orqali to'g'ridan-to'g'ri toggle qiladi (backend `feature_`-only + audit). O'zgarish
 * A14.08 o'zgarishlar-jurnaliда ham ko'rinadi. AUDIT_VIEW-gated (yozuvni backend admin-gate qiladi).
 */
export function FeatureFlagToggle() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => setFlags(r.rows))
      .catch(() => setFlags([]))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const list = flags ?? []
    return s ? list.filter((f) => f.key.toLowerCase().includes(s)) : list
  }, [flags, q])

  const toggle = (f: AdminFeatureFlag) => {
    if (busy) return
    const next = !f.enabled
    setBusy(f.key)
    setFeatureFlag(f.key, { enabled: next })
      .then(() => {
        setFlags((fs) => fs?.map((x) => (x.key === f.key ? { ...x, enabled: next, value: next ? 'true' : 'false' } : x)) ?? null)
        toast(next ? t.flags.on : t.flags.off, { tone: 'success' })
      })
      .catch((e: unknown) => toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) }))
      .finally(() => setBusy(null))
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.flags.toggleTitle}
        screenId="A14.03"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <div className="flex items-center gap-2 rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5">
          <Search size={14} className="text-tertiary" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.loc.searchPh}
            className="w-full bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {flags === null ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-tertiary">
            <Flag size={22} className="mx-auto mb-2 opacity-60" />
            {t.states.emptyTitle}
          </div>
        ) : (
          <div className="max-w-2xl space-y-2">
            {filtered.map((f) => (
              <button
                key={f.key}
                onClick={() => toggle(f)}
                disabled={busy === f.key}
                className="flex w-full items-center justify-between rounded-lg border border-border-default bg-surface-1 px-4 py-3 text-left hover:bg-surface-2 disabled:opacity-60"
              >
                <span className="min-w-0 flex-1 truncate font-mono text-sm text-primary" title={f.key}>{f.key}</span>
                <span
                  className={cn(
                    'relative ml-3 h-5 w-9 shrink-0 rounded-full transition-colors',
                    f.enabled ? 'bg-accent' : 'bg-surface-3',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                      f.enabled ? 'translate-x-4' : 'translate-x-0.5',
                    )}
                  />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
